/**
 * "Vous pourriez aussi aimer" — maillage interne automatique entre toutes les pages contenu.
 *
 * Stratégie de matching :
 *  - Trouve les pages de TOUS les types (guides, cas, comparatifs) qui partagent
 *    la même catégorie OU le même mot-clé que la page courante.
 *  - Retourne max 6 suggestions, types mélangés (diversité).
 *
 * Bénéfice SEO : maillage interne profond = signal pertinence Google + parcours utilisateur prolongé
 * = signal LLM (graph d'entités cohérent).
 */
import { GUIDES } from "@/data/guidesData";
import { CASE_STUDIES } from "@/data/caseStudiesData";
import { COMPARISONS } from "@/data/comparisonsData";

const TYPE_LABELS = {
  guide: { path: "/guides", label: "Guide", color: "red" },
  case: { path: "/etudes-de-cas", label: "Cas client", color: "emerald" },
  comparison: { path: "/comparatifs", label: "Comparatif", color: "blue" },
};

function tagAll() {
  const out = [];
  for (let i = 0; i < GUIDES.length; i += 1) {
    out.push({ type: "guide", item: GUIDES[i] });
  }
  for (let i = 0; i < CASE_STUDIES.length; i += 1) {
    out.push({ type: "case", item: CASE_STUDIES[i] });
  }
  for (let i = 0; i < COMPARISONS.length; i += 1) {
    out.push({ type: "comparison", item: COMPARISONS[i] });
  }
  return out;
}

/**
 * Score de similarité entre 2 contenus (1-10).
 * Plus le score est haut, plus les contenus sont proches.
 */
function scoreSimilarity(current, candidate) {
  let score = 0;
  // Catégorie identique = +5
  if (current.category === candidate.category) score += 5;
  // Mot-clé identique = +3 (sur base partagée)
  if (current.keyword && candidate.keyword) {
    const cur = current.keyword.toLowerCase();
    const cand = candidate.keyword.toLowerCase();
    const curWords = cur.split(/\s+/);
    for (let i = 0; i < curWords.length; i += 1) {
      const w = curWords[i];
      if (w.length > 4 && cand.includes(w)) score += 1;
    }
  }
  // Title partage des mots significatifs (>5 lettres)
  if (current.title && candidate.title) {
    const curWords = current.title.toLowerCase().split(/\s+/);
    for (let i = 0; i < curWords.length; i += 1) {
      const w = curWords[i];
      if (w.length > 5 && candidate.title.toLowerCase().includes(w)) score += 0.5;
    }
  }
  return score;
}

/**
 * Retourne les 6 suggestions les plus pertinentes pour une page contenu donnée.
 *
 * @param {Object} current - le contenu courant (avec slug, category, keyword, title)
 * @param {string} currentType - "guide" | "case" | "comparison"
 * @returns {Array<{type, path, label, color, title, slug, category}>}
 */
export function getRelatedContent(current, currentType) {
  const all = tagAll();
  const currentSlug = current.slug;
  const scored = [];
  for (let i = 0; i < all.length; i += 1) {
    const entry = all[i];
    if (entry.item.slug === currentSlug && entry.type === currentType) continue;
    const score = scoreSimilarity(current, entry.item);
    if (score > 0) {
      scored.push({ ...entry, score });
    }
  }
  scored.sort((a, b) => b.score - a.score);

  // Diversité : on prend max 3 du même type
  const limits = { guide: 3, case: 3, comparison: 3 };
  const used = { guide: 0, case: 0, comparison: 0 };
  const result = [];
  for (let i = 0; i < scored.length; i += 1) {
    const entry = scored[i];
    if (used[entry.type] >= limits[entry.type]) continue;
    used[entry.type] += 1;
    const meta = TYPE_LABELS[entry.type];
    result.push({
      type: entry.type,
      path: `${meta.path}/${entry.item.slug}`,
      label: meta.label,
      color: meta.color,
      title: entry.item.title,
      slug: entry.item.slug,
      category: entry.item.category,
    });
    if (result.length >= 6) break;
  }
  return result;
}
